import React, { useEffect, useState } from "react";
import Pagination from "../../../../components/Pagination";
import api from "../../../../../utils/axios";
import { useEscapeClose } from "../../../../../utils/SamuiUtils";
import {
  useModalKeyboardNavigation,
  getKeyboardInstructions,
} from "../../../../../utils/ModalKeyboardNavigation";

export default function CustomerModal({ isOpen, onClose, onSelect }) {
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEscapeClose(isOpen, onClose);

  useModalKeyboardNavigation(
    isOpen,
    filtered,
    selectedRowIndex,
    setSelectedRowIndex,
    null,
    onClose,
    {
      customEnterHandler: () => handleSelect(filtered[selectedRowIndex]),
      includeHighlightedOnEnter: true,
    }
  );

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    api
      .get(`/get-ar`)
      .then((res) => {
        if (res.data.status === "OK") {
          const list =
            res.data.result?.recordset || res.data.result || [];
          setCustomers(list);
          setFiltered(list);
        } else {
          setCustomers([]);
          setFiltered([]);
        }
      })
      .catch((err) => {
        console.error("โหลดลูกค้าล้มเหลว:", err);
        setCustomers([]);
        setFiltered([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setCustomers([]);
      setFiltered([]);
      setSelectedRowIndex(0);
      setCurrentPage(1);
    }
  }, [isOpen]);

  const getDisplayName = (c) => {
    const full = `${c.AR_Name || ""} ${c.AR_LastName || ""}`.trim();
    if (full) return full;

    if (Array.isArray(c.Contacts)) {
      const contact = c.Contacts.find((ct) => ct.AR_ContactName);
      if (contact) return contact.AR_ContactName;
    }

    return "ไม่ทราบชื่อลูกค้า";
  };

  //  ค้นหา
  useEffect(() => {
    const f = customers.filter(
      (c) =>
        getDisplayName(c).toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.AR_Id?.toString().includes(searchTerm)
    );
    setFiltered(f);
    setSelectedRowIndex(0);
    setCurrentPage(1);
  }, [searchTerm, customers]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  const handleSelect = (cus) => {
    if (!cus) return;
    onSelect(cus);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal fade ${isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content border-0 shadow-lg">

            {/*  Header */}
            <div
              className="modal-header"
              style={{ backgroundColor: "#E13F57", color: "white" }}
            >
              <h4 className="modal-title"><b>เลือกลูกค้า</b></h4>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            {/*  Body */}
            <div className="modal-body">
              {/* Search */}
              <div className="form-group mb-3 d-flex justify-between items-center">
                <input
                  type="text"
                  className="form-control"
                  placeholder="ค้นหา ชื่อลูกค้า / รหัสลูกค้า..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Table */}
              <div className="table-responsive" style={{ maxHeight: "450px" }}>
                <table
                  className="table table-striped table-hover text-sm"
                  style={{ fontSize: "14px" }}
                >
                  <thead style={{ backgroundColor: "#E13F57", color: "white" }}>
                    <tr>
                      <th className="text-center" style={{ width: "20%", color: "white" }}>
                        รหัสลูกค้า
                      </th>
                      <th style={{ color: "white" }}>ชื่อลูกค้า</th>
                      <th className="text-center" style={{ width: "20%", color: "white" }}>
                        การเลือก
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="3" className="text-center py-4 text-muted">
                          กำลังโหลดข้อมูล...
                        </td>
                      </tr>
                    ) : paginated.length > 0 ? (
                      paginated.map((c, idx) => {
                        const isHighlighted = idx === selectedRowIndex;
                        return (
                          <tr
                            key={c.AR_Id}
                            onClick={() => handleSelect(c)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isHighlighted
                                ? "rgba(225,63,87,0.2)"
                                : "transparent",
                            }}
                          >
                            <td className="text-center">{c.AR_Id}</td>
                            <td>{getDisplayName(c)}</td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(c);
                                }}
                              >
                                เลือก
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-4 text-muted">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-2 border-top pt-2">
                <Pagination
                  totalItems={filtered.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(val) => {
                    setItemsPerPage(val);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            {/*  Footer */}
            <div className="modal-footer">
              <div
                className="text-left text-muted mr-auto"
                style={{ fontSize: "12px" }}
              >
                {getKeyboardInstructions("ลูกค้า", true)}
              </div>
              <button className="btn btn-secondary" onClick={onClose}>
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>

      {isOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
}
